import {useTranslation} from 'react-i18next'
import {LienzoSvg, corto} from '@/components/analisis/lienzo-svg'

/**
 * Solo lectura, derivada del grafo de causas; el backend dibuja la misma para el informe 1401 (`core/espina_png.py`).
 * Trunca solo en el dibujo: el `<title>` de cada causa lleva el enunciado completo.
 */
export type EspinaData = {key: string; label: string; causas: string[]}

const MAX_CAUSAS_DIBUJADAS = 4
// El texto de un SVG no se envuelve solo: la cabeza, de ancho fijo, entra en dos renglones de este largo.
const CARACTERES_POR_RENGLON = 26
const RENGLONES_CABEZA = 2

const ANCHO = 1000
const ALTO = 460
const EJE_Y = 230
const EJE_X0 = 16
const CABEZA_X = 726
const LARGO_HUESO_X = 108
const LARGO_HUESO_Y = 150
const TICK = 52

/** Parte el enunciado en hasta dos renglones por palabras; lo que no cabe se trunca. */
function renglonesDe(texto: string): string[] {
  const palabras = texto.split(/\s+/).filter(Boolean)
  const renglones: string[] = []
  let actual = ''
  for (const palabra of palabras) {
    const tentativa = actual ? `${actual} ${palabra}` : palabra
    if (tentativa.length <= CARACTERES_POR_RENGLON) {
      actual = tentativa
      continue
    }
    renglones.push(actual || corto(palabra, CARACTERES_POR_RENGLON))
    actual = actual ? palabra : ''
    if (renglones.length === RENGLONES_CABEZA) break
  }
  if (actual && renglones.length < RENGLONES_CABEZA) renglones.push(actual)
  const sobra = texto.split(/\s+/).join(' ').length > renglones.join(' ').length
  return sobra ? [...renglones.slice(0, -1), corto(`${renglones.at(-1)}…`, CARACTERES_POR_RENGLON)] : renglones
}

type LineaEspina = {texto: string; completo: string | null}

function lineasDe(causas: string[], masLabel: (n: number) => string): LineaEspina[] {
  const dibujadas = causas.slice(0, MAX_CAUSAS_DIBUJADAS).map((c) => ({texto: corto(c), completo: c}))
  const sobran = causas.length - MAX_CAUSAS_DIBUJADAS
  return sobran > 0 ? [...dibujadas, {texto: masLabel(sobran), completo: null}] : dibujadas
}

function Hueso({
  espina,
  baseX,
  arriba,
  masLabel,
}: {
  espina: EspinaData
  baseX: number
  arriba: boolean
  masLabel: (n: number) => string
}) {
  const signo = arriba ? -1 : 1
  const puntaX = baseX - LARGO_HUESO_X
  const puntaY = EJE_Y + signo * LARGO_HUESO_Y
  const lineas = lineasDe(espina.causas, masLabel)
  return (
    <g data-testid={`espina-hueso-${espina.key}`}>
      <line
        x1={baseX}
        y1={EJE_Y}
        x2={puntaX}
        y2={puntaY}
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        className="text-primary/40"
      />
      <text
        x={puntaX}
        y={puntaY + signo * 16}
        textAnchor="middle"
        fill="currentColor"
        className="fill-primary font-mono text-[13px] font-semibold tracking-[0.08em] uppercase">
        {espina.label}
      </text>
      {lineas.map((linea, i) => {
        // Arriba y abajo se recorren al revés para que la espina se lea siempre de arriba hacia abajo.
        const t = arriba ? 0.86 - i * 0.19 : 0.29 + i * 0.19
        const x = baseX + (puntaX - baseX) * t
        const y = EJE_Y + (puntaY - EJE_Y) * t
        return (
          <g key={linea.texto + String(i)}>
            <line
              x1={x}
              y1={y}
              x2={x + TICK}
              y2={y}
              stroke="currentColor"
              strokeWidth={1.4}
              className="text-primary/30"
            />
            <text
              x={x + TICK + 6}
              y={y + 4}
              fill="currentColor"
              className="fill-foreground text-[13px]"
              data-testid="espina-causa">
              {linea.completo && <title>{linea.completo}</title>}
              {linea.texto}
            </text>
          </g>
        )
      })}
    </g>
  )
}

export function EspinaDePescado({espinas, problema}: {espinas: EspinaData[]; problema: string}) {
  const {t} = useTranslation()
  const masLabel = (n: number) => t('analisis.espina.masCausas', {count: n})
  const renglones = renglonesDe(problema)
  const arriba = espinas.filter((_, i) => i % 2 === 0)
  const abajo = espinas.filter((_, i) => i % 2 === 1)

  return (
    <LienzoSvg
      testId="espina-de-pescado"
      ariaLabel={t('analisis.espina.aria', {problema})}
      ancho={ANCHO}
      alto={ALTO}
      anchoMinimo={760}>
      <defs>
        <marker id="espina-punta" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
          <path d="M0 0 L9 4.5 L0 9 z" fill="currentColor" className="fill-primary" />
        </marker>
      </defs>
      <line
        x1={EJE_X0}
        y1={EJE_Y}
        x2={CABEZA_X}
        y2={EJE_Y}
        stroke="currentColor"
        strokeWidth={3.5}
        className="text-primary"
        markerEnd="url(#espina-punta)"
      />
      <rect
        x={CABEZA_X + 12}
        y={EJE_Y - 34}
        width={ANCHO - CABEZA_X - 24}
        height={68}
        rx={12}
        fill="currentColor"
        className="fill-primary"
      />
      <text fill="currentColor" className="fill-primary-foreground text-[14px] font-semibold">
        <title>{problema}</title>
        {renglones.map((renglon, i) => (
          <tspan key={renglon + String(i)} x={CABEZA_X + 26} y={EJE_Y + (renglones.length > 1 ? -3 + i * 19 : 5)}>
            {renglon}
          </tspan>
        ))}
      </text>
      {[
        [arriba, true],
        [abajo, false],
      ].map(([fila, esArriba]) => {
        const grupos = fila as EspinaData[]
        const paso = (CABEZA_X - 130) / Math.max(grupos.length, 1)
        return grupos.map((espina, k) => (
          <Hueso
            key={espina.key}
            espina={espina}
            baseX={150 + paso * k + paso * 0.55}
            arriba={esArriba as boolean}
            masLabel={masLabel}
          />
        ))
      })}
    </LienzoSvg>
  )
}
